// Tricky Items data structure and logic for Tricky Tic-Tac-Toe challenge

interface TrickyItem {
  base: string;
  canHaveNumber: boolean;
  mustHaveNumber: boolean;
  maxNumber: number;
  canHaveColor: boolean;
  canBeNone?: boolean;
  isTheme?: boolean;
  isBasicColor?: boolean;
  isAdvancedColor?: boolean;
}

interface TrickyItemsData {
  trickyItems: TrickyItem[];
  basicColors: string[];
  basicColorsDescriptorOnly: string[];
  colorModifiers: string[];
  advancedColors: string[];
  colorGroups: { [key: string]: string[] };
  numberWords: string[];
  numberPrefixes: string[];
}

class TRICKY_INGREDIENTS {
  public getAdvancedColors(): string[] {
    return this.data.advancedColors;
  }
  private data: TrickyItemsData;

  constructor(data: TrickyItemsData) {
    this.data = data;
  }

  // Generate a weighted random number
  private getWeightedRandomNumber(maxNumber: number): string {
    const numbers = this.data.numberWords.filter(num => parseInt(num) <= maxNumber);
    
  // Use uniform distribution for all cases
  return numbers[Math.floor(Math.random() * numbers.length)];
  }

  // Convert item to singular form when number is 1
  private makeSingular(item: string): string {
    // Special cases
    if (item === 'leaves') {
      return 'leaf';
    }
    if (item === 'butterflies') {
      return 'butterfly';
    }
    if (item === 'punches') {
      return 'punch';
    }
    
    // General rule: remove 's' from the end if it ends with 's'
    if (item.endsWith('s') && item.length > 1) {
      // Handle special cases where we shouldn't just remove 's'
      if (item.endsWith('ss') || item.endsWith('us') || item === 'crosses') {
        return item; // Don't change words ending in 'ss', 'us', or 'crosses'
      }
      return item.slice(0, -1);
    }
    
    return item; // Return unchanged if it doesn't end with 's'
  }

  // Capitalize the first letter of a string
  private capitalizeFirst(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // Generate a standalone color item
  private generateStandaloneColor(item: TrickyItem, usedColorGroups?: Set<string>): { color: string; colorGroup?: string } {
    if (item.isBasicColor) {
      // Basic colors MUST have a modifier when used alone (but only certain colors can be standalone)
      const availableBasicColors = this.data.basicColors.filter(color => {
        const colorGroup = this.getColorGroup(color);
        return !colorGroup || !usedColorGroups?.has(colorGroup);
      });
      
      if (availableBasicColors.length > 0) {
        const modifier = this.data.colorModifiers[Math.floor(Math.random() * this.data.colorModifiers.length)];
        const basicColor = availableBasicColors[Math.floor(Math.random() * availableBasicColors.length)];
        const colorGroup = this.getColorGroup(basicColor);
        return {
          color: this.capitalizeFirst(`${modifier} ${basicColor}`),
          colorGroup: colorGroup || undefined
        };
      }
    } else if (item.isAdvancedColor) {
      // Advanced colors can be used alone without modifiers
      const availableAdvancedColors = this.data.advancedColors.filter(color => {
        const colorGroup = this.getColorGroup(color);
        return !colorGroup || !usedColorGroups?.has(colorGroup);
      });
      
      if (availableAdvancedColors.length > 0) {
        const advancedColor = availableAdvancedColors[Math.floor(Math.random() * availableAdvancedColors.length)];
        const colorGroup = this.getColorGroup(advancedColor);
        return {
          color: this.capitalizeFirst(advancedColor),
          colorGroup: colorGroup || undefined
        };
      }
    }
    return { color: item.base }; // fallback
  }

  // Get the color group for a given color
  private getColorGroup(color: string): string | null {
    for (const [group, colors] of Object.entries(this.data.colorGroups)) {
      if (colors.includes(color)) {
        return group;
      }
    }
    return null;
  }

  // Generate a single non-theme tricky item with possible modifiers
  private async generateNonThemeItem(): Promise<string> {
    const nonThemeItems = this.data.trickyItems.filter(item => !item.isTheme);
    const randomItem = nonThemeItems[Math.floor(Math.random() * nonThemeItems.length)];
    
    // Handle special color items
    if (randomItem.isBasicColor || randomItem.isAdvancedColor) {
      const colorResult = this.generateStandaloneColor(randomItem);
      return colorResult.color;
    }
    
    let numberPart = '';
    let colorPart = '';
    const basePart = randomItem.base;
    let selectedNumber = 0;
    
    // Check if this is a shape item that can have "layered" modifier
    const shapeItems = ['circles', 'squares', 'diamond shapes', 'hearts', 'triangles', 'ovals', 'hexagons', 'stars'];
    const isShapeItem = shapeItems.includes(randomItem.base);
    
    // Small chance (5%) for shapes to be "layered" instead of normal modifiers
    if (isShapeItem && Math.random() < 0.05) {
      return `layered ${basePart}`;
    }
    
    // Decide what type of modifier to add (roughly equal split)
    const modifierChoice = Math.random();
    const shouldAddNumber = randomItem.canHaveNumber && (randomItem.mustHaveNumber || modifierChoice < 0.45); // 45% chance
    const shouldAddColor = randomItem.canHaveColor && modifierChoice >= 0.45 && modifierChoice < 0.9; // 45% chance
    const shouldAddBoth = randomItem.canHaveNumber && randomItem.canHaveColor && modifierChoice >= 0.9; // 10% chance
    
    // Add number if chosen
    if (shouldAddNumber || shouldAddBoth) {
      const randomNumber = this.getWeightedRandomNumber(randomItem.maxNumber);
      selectedNumber = parseInt(randomNumber);
      
      // Favor "at least" over "exactly" (70% vs 30%)
      const prefixChoice = Math.random();
      let prefix: string;
      if (prefixChoice < 0.7) {
        prefix = "at least"; // 70% chance
      } else {
        prefix = "exactly"; // 30% chance
      }
      
      numberPart = `${prefix} ${randomNumber}`;
    }
    
    // Add color if chosen (but not if number > 4, unless it's the only modifier)
    if ((shouldAddColor || shouldAddBoth) && (selectedNumber <= 4 || (!shouldAddNumber && !shouldAddBoth))) {
      const shouldUseAdvancedColor = Math.random() < 0.4; // 40% chance for advanced colors
      
      if (shouldUseAdvancedColor) {
        // Use advanced colors (can be used alone)
        colorPart = this.data.advancedColors[Math.floor(Math.random() * this.data.advancedColors.length)];
      } else {
        // Use basic colors (when used as descriptors for other items, no modifier needed)
        const allBasicColors = [...this.data.basicColors, ...this.data.basicColorsDescriptorOnly];
        const basicColor = allBasicColors[Math.floor(Math.random() * allBasicColors.length)];
        colorPart = basicColor;
      }
    }
    
    // Use singular form if the number is 1
    let finalBasePart = basePart;
    if (selectedNumber === 1) {
      finalBasePart = this.makeSingular(basePart);
    }
    
    // Combine parts in correct order: number + color + base
    const parts = [numberPart, colorPart, finalBasePart].filter(part => part !== '');
    return this.capitalizeFirst(parts.join(' '));
  }

  // Generate a single theme item (no modifiers)
  private generateThemeItem(): string {
    const themeItems = this.data.trickyItems.filter(item => item.isTheme);
    const randomThemeItem = themeItems[Math.floor(Math.random() * themeItems.length)];
    return this.capitalizeFirst(randomThemeItem.base);
  }

  // Generate a "no" item (e.g., "no hearts", "no watercolor")
  private generateNoItem(): string | null {
    const noItems = this.data.trickyItems.filter(item => item.canBeNone);
    if (noItems.length === 0) {
      return null;
    }
    const randomNoItem = noItems[Math.floor(Math.random() * noItems.length)];
    return this.capitalizeFirst(`no ${randomNoItem.base}`);
  }

  // Generate a unique "no" item that hasn't been used yet
  private generateUniqueNoItem(usedBaseItems: Set<string>): { item: string; baseItem: string } | null {
    const noItems = this.data.trickyItems.filter(item => item.canBeNone && !usedBaseItems.has(item.base));
    if (noItems.length === 0) {
      return null;
    }
    const randomNoItem = noItems[Math.floor(Math.random() * noItems.length)];
    return {
      item: this.capitalizeFirst(`no ${randomNoItem.base}`),
      baseItem: randomNoItem.base
    };
  }

  // Generate a unique theme item that hasn't been used yet
  private generateUniqueThemeItem(usedBaseItems: Set<string>): { item: string; baseItem: string } | null {
    const themeItems = this.data.trickyItems.filter(item => item.isTheme && !usedBaseItems.has(item.base));
    if (themeItems.length === 0) {
      return null;
    }
    const randomThemeItem = themeItems[Math.floor(Math.random() * themeItems.length)];
    return {
      item: this.capitalizeFirst(randomThemeItem.base),
      baseItem: randomThemeItem.base
    };
  }

  // Generate a unique non-theme item that hasn't been used yet
  private async generateUniqueNonThemeItem(usedBaseItems: Set<string>, layoutDesigns?: string[], layoutDesignUsed?: boolean, photoRequirements?: string[], photoRequirementUsed?: boolean, backgroundTypes?: string[], backgroundTypeUsed?: boolean, titleTypes?: string[], titleTypeUsed?: boolean, wordItems?: string[], wordItemCount?: number, maxWordItems?: number, usedColorGroups?: Set<string>): Promise<{ item: string; baseItem: string; colorGroup?: string } | null> {
    // Filter out layout designs if one has already been used
    let availableItems = this.data.trickyItems.filter(item => !item.isTheme && !usedBaseItems.has(item.base));
    
    if (layoutDesignUsed && layoutDesigns) {
      availableItems = availableItems.filter(item => !layoutDesigns.includes(item.base));
    }
    
    // Filter out photo requirements if one has already been used
    if (photoRequirementUsed && photoRequirements) {
      availableItems = availableItems.filter(item => !photoRequirements.includes(item.base));
    }
    
    // Filter out background types if one has already been used
    if (backgroundTypeUsed && backgroundTypes) {
      availableItems = availableItems.filter(item => !backgroundTypes.includes(item.base));
    }
    
    // Filter out title types if one has already been used
    if (titleTypeUsed && titleTypes) {
      availableItems = availableItems.filter(item => !titleTypes.includes(item.base));
    }
    
    // Filter out word items if we've reached the limit
    if (wordItems && wordItemCount !== undefined && maxWordItems !== undefined && wordItemCount >= maxWordItems) {
      availableItems = availableItems.filter(item => !wordItems.includes(item.base));
    }
    
    if (availableItems.length === 0) {
      return null;
    }
    
    const randomItem = availableItems[Math.floor(Math.random() * availableItems.length)];
    
    // Handle special color items
    if (randomItem.isBasicColor || randomItem.isAdvancedColor) {
      const colorResult = this.generateStandaloneColor(randomItem, usedColorGroups);
      return {
        item: colorResult.color,
        baseItem: randomItem.base,
        colorGroup: colorResult.colorGroup
      };
    }
    
    let numberPart = '';
    let colorPart = '';
    const basePart = randomItem.base;
    let selectedNumber = 0;
    
    // Check if this should be a layered item (only for specific items)
    const layeredItems = [
      "patterned paper",
      "cardstock",
      "squares",
      "stars",
      "circles",
      "hexagons",
      "triangles",
      "hearts"
    ];
    if (layeredItems.includes(randomItem.base) && Math.random() < 0.3) { // 30% chance
      return {
        item: this.capitalizeFirst(`layered ${basePart}`),
        baseItem: randomItem.base
      };
    }
    
    // Decide what type of modifier to add (roughly equal split)
    const modifierChoice = Math.random();
    const shouldAddNumber = randomItem.canHaveNumber && (randomItem.mustHaveNumber || modifierChoice < 0.45); // 45% chance
    const shouldAddColor = randomItem.canHaveColor && modifierChoice >= 0.45 && modifierChoice < 0.9; // 45% chance
    const shouldAddBoth = randomItem.canHaveNumber && randomItem.canHaveColor && modifierChoice >= 0.9; // 10% chance
    
    // Add number if chosen
    if (shouldAddNumber || shouldAddBoth) {
      const randomNumber = this.getWeightedRandomNumber(randomItem.maxNumber);
      selectedNumber = parseInt(randomNumber);
      
      // Favor "at least" over "exactly" (70% vs 30%)
      const prefixChoice = Math.random();
      let prefix: string;
      if (prefixChoice < 0.7) {
        prefix = "at least"; // 70% chance
      } else {
        prefix = "exactly"; // 30% chance
      }
      
      numberPart = `${prefix} ${randomNumber}`;
    }
    
    // Add color if chosen (but not if number > 2, unless it's the only modifier)
    // Only allow basic colors (and descriptor-only) as descriptors for items. Advanced colors are never used as descriptors.
    if ((shouldAddColor || shouldAddBoth) && (selectedNumber <= 2 || (!shouldAddNumber && !shouldAddBoth))) {
      const allBasicColors = [...this.data.basicColors, ...this.data.basicColorsDescriptorOnly];
      const availableBasicColors = allBasicColors.filter(color => {
        const colorGroup = this.getColorGroup(color);
        return !colorGroup || !usedColorGroups?.has(colorGroup);
      });
      if (availableBasicColors.length > 0) {
        colorPart = availableBasicColors[Math.floor(Math.random() * availableBasicColors.length)];
      } else {
        // Fallback to any basic color if all groups are used
        const basicColor = allBasicColors[Math.floor(Math.random() * allBasicColors.length)];
        colorPart = basicColor;
      }
    }
    
    // Use singular form if the number is 1
    let finalBasePart = basePart;
    if (selectedNumber === 1) {
      finalBasePart = this.makeSingular(basePart);
    }
    
    // Combine parts in correct order: number + color + base
    const parts = [numberPart, colorPart, finalBasePart].filter(part => part !== '');
    const colorGroup = colorPart ? this.getColorGroup(colorPart) : undefined;
    return {
      item: this.capitalizeFirst(parts.join(' ')),
      baseItem: randomItem.base,
      colorGroup: colorGroup || undefined
    };
  }

  // Generate 9 tricky items for tic-tac-toe grid with at most one theme item and very rarely one "no" item
  async pickRandom(numEntries: number): Promise<string[]> {
    const result: string[] = [];
    const usedBaseItems = new Set<string>(); // Track used base items to prevent duplicates

    // Track layout designs separately (only one allowed per challenge)
    const layoutDesigns = ['vertical design', 'horizontal design', '3x2 grid design', '2x2 grid design', '3x3 grid design', 'large circular design'];
    let layoutDesignUsed = false;

    // Track photo requirements separately (only one allowed per challenge)
    const photoRequirements = ['photos', 'square photos', 'large photo', 'tiny photos', 'double matted photos', 'matted photos', 'landscape orientated photos', 'portrait orientated photos', 'black & white photos', '1 6"x4" photo', 'old photos', 'recent photos', 'a photo with nobody in it', 'a photo of an animal'];
    let photoRequirementUsed = false;

    // Track background types separately (only one allowed per challenge)
    const backgroundTypes = ['white background', 'black background', 'patterned paper background'];
    let backgroundTypeUsed = false;

    // Track title types separately (only one allowed per challenge)
    const titleTypes = ['large title', 'song title as title', 'word title', 'title', 'number in the title', 'stamped title', 'mixed font title'];
    let titleTypeUsed = false;

    // Track word items separately (usually only one, 3% chance for two)
    const wordItems = ["the word 'love'", "the word 'adventure'", "the word 'home'", "the word 'fun'", "the word 'memories'", "the word 'happy'", "the word 'family'", "the word 'journey'", "the word 'celebrate'", "the word 'together'", "the word 'dream'", "the word 'smile'", "the word 'magical'", "the word 'forever'", "the word 'special'", "the word 'amazing'", "the word 'precious'", "the word 'create'", "the word 'explore'"];
    let wordItemCount = 0;
    const allowTwoWordItems = Math.random() < 0.03; // 3% chance to allow 2 word items
    const maxWordItems = allowTwoWordItems ? 2 : 1;

    // Track color groups to prevent too many similar colors (only one per group per challenge)
    const usedColorGroups = new Set<string>();

  // Track number of standalone color items only
  let standaloneColorItemCount = 0;
  const maxStandaloneColorItems = 2;

    // Decide if we should include a theme item (33% chance)
    const shouldIncludeTheme = Math.random() < 0.33;
    let themeItemAdded = false;

    // Very rarely (5% chance) include a "no" item, and only one per challenge
    const shouldIncludeNoItem = Math.random() < 0.05;
    let noItemAdded = false;

    for (let i = 0; i < numEntries; i++) {
      let itemGenerated = false;
      let attempts = 0;
      const maxAttempts = 50; // Prevent infinite loops

      while (!itemGenerated && attempts < maxAttempts) {
        attempts++;

        // Only allow up to maxStandaloneColorItems standalone color items
        if (standaloneColorItemCount < maxStandaloneColorItems && Math.random() < 0.25) {
          // Pick either advanced or basic color (50/50)
          const useAdvanced = Math.random() < 0.5;
          if (useAdvanced && this.data.advancedColors.length > 0) {
            // Pick an unused advanced color group if possible
            const availableAdvancedColors = this.data.advancedColors.filter(color => {
              const colorGroup = this.getColorGroup(color);
              return !colorGroup || !usedColorGroups.has(colorGroup);
            });
            if (availableAdvancedColors.length > 0) {
              const advancedColor = availableAdvancedColors[Math.floor(Math.random() * availableAdvancedColors.length)];
              const colorGroup = this.getColorGroup(advancedColor);
              result.push(this.capitalizeFirst(advancedColor));
              if (colorGroup) usedColorGroups.add(colorGroup);
              standaloneColorItemCount++;
              itemGenerated = true;
              continue;
            }
          } else if (this.data.basicColors.length > 0 && this.data.colorModifiers.length > 0) {
            // Pick an unused basic color group if possible
            const availableBasicColors = this.data.basicColors.filter(color => {
              const colorGroup = this.getColorGroup(color);
              return !colorGroup || !usedColorGroups.has(colorGroup);
            });
            if (availableBasicColors.length > 0) {
              const basicColor = availableBasicColors[Math.floor(Math.random() * availableBasicColors.length)];
              const modifier = this.data.colorModifiers[Math.floor(Math.random() * this.data.colorModifiers.length)];
              const colorGroup = this.getColorGroup(basicColor);
              result.push(this.capitalizeFirst(`${modifier} ${basicColor}`));
              if (colorGroup) usedColorGroups.add(colorGroup);
              standaloneColorItemCount++;
              itemGenerated = true;
              continue;
            }
          }
        }

        if (shouldIncludeNoItem && !noItemAdded && Math.random() < 0.15) {
          // Try to add a "no" item (15% chance per slot, but only if we haven't added one yet)
          const noItem = this.generateUniqueNoItem(usedBaseItems);
          if (noItem) {
            result.push(noItem.item);
            usedBaseItems.add(noItem.baseItem);
            noItemAdded = true;
            itemGenerated = true;
            continue;
          }
        }

        if (shouldIncludeTheme && !themeItemAdded && Math.random() < 0.2) {
          // Try to add a theme item (20% chance per slot, but only if we haven't added one yet)
          const themeItem = this.generateUniqueThemeItem(usedBaseItems);
          if (themeItem) {
            result.push(themeItem.item);
            usedBaseItems.add(themeItem.baseItem);
            themeItemAdded = true;
            itemGenerated = true;
          }
        } else {
          // Try to add a regular item
          const trickyItem = await this.generateUniqueNonThemeItem(usedBaseItems, layoutDesigns, layoutDesignUsed, photoRequirements, photoRequirementUsed, backgroundTypes, backgroundTypeUsed, titleTypes, titleTypeUsed, wordItems, wordItemCount, maxWordItems, usedColorGroups);
          if (trickyItem) {
            // If this is a standalone color and we've hit the limit, skip it
            const isStandaloneColor = this.data.trickyItems.find(
              (item) => item.base === trickyItem.baseItem && (item.isBasicColor || item.isAdvancedColor)
            );
            if (isStandaloneColor && standaloneColorItemCount >= maxStandaloneColorItems) {
              // Don't add more standalone colors
              continue;
            }
            // If this is a standalone color and we haven't hit the limit, increment the count
            if (isStandaloneColor) {
              standaloneColorItemCount++;
            }
            result.push(trickyItem.item);
            usedBaseItems.add(trickyItem.baseItem);

            // Check if this item is a layout design
            if (layoutDesigns.includes(trickyItem.baseItem)) {
              layoutDesignUsed = true;
            }

            // Check if this item is a photo requirement
            if (photoRequirements.includes(trickyItem.baseItem)) {
              photoRequirementUsed = true;
            }

            // Check if this item is a background type
            if (backgroundTypes.includes(trickyItem.baseItem)) {
              backgroundTypeUsed = true;
            }

            // Check if this item is a title type
            if (titleTypes.includes(trickyItem.baseItem)) {
              titleTypeUsed = true;
            }

            // Check if this item is a word item
            if (wordItems.includes(trickyItem.baseItem)) {
              wordItemCount++;
            }

            // Track color group if one was used (for both standalone colors and descriptor colors)
            if (trickyItem.colorGroup) {
              usedColorGroups.add(trickyItem.colorGroup);
            }

            itemGenerated = true;
          }
        }
      }

      // If we couldn't generate a unique item after max attempts, fallback to generating without uniqueness constraint
      if (!itemGenerated) {
        console.warn(`Failed to generate unique item for slot ${i}, using fallback`);
        // Try to generate a fallback item without uniqueness constraint
        try {
          const fallbackItem = await this.generateNonThemeItem();
          result.push(fallbackItem);
        } catch (error) {
          // Ultimate fallback - add a simple item
          result.push(this.capitalizeFirst("buttons"));
        }
      }
    }
    
    return result;
  }
}

// Function to load tricky ingredients data
async function getTrickyIngredients(): Promise<TRICKY_INGREDIENTS> {
  try {
    const response = await fetch('/data/trickyItems.json');
    const data: TrickyItemsData = await response.json();
    return new TRICKY_INGREDIENTS(data);
  } catch (error) {
    console.error('Error loading tricky ingredients:', error);
    throw error;
  }
}

export default getTrickyIngredients;
export { TRICKY_INGREDIENTS };
export type { TrickyItem, TrickyItemsData };
